import React, { useContext, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid2";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Badge,
  CircularProgress,
  Button,
  TablePagination,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import PageBody from "../GeneralComponents/PageBody";
import ListEmpty from "../GeneralComponents/ListEmpty";
import InputFilter from "../GeneralComponents/InputFilter";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";

const TIPO_LABELS = {
  INICIO_SERVICIO: "INICIO DE SERVICIO",
  RECEPCION_OBRA: "RECEPCION OBRA",
  BAJA: "BAJA",
  RECTIFICACION: "RECTIFICACIÓN",
};

const MESES = [
  "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
  "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE",
];

const labelTipo = (t) => TIPO_LABELS[t] || (t ? String(t).replace(/_/g, " ") : "");

const fmtFecha = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const padOficio = (n) => {
  const s = String(n ?? "");
  return s.length < 4 ? s.padStart(4, "0") : s;
};

export default function HistorialOficios() {
  const { setPageTitle } = useContext(GeneralContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    setPageTitle("Historial de oficios");
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await axios.getOficiosHistorial();
      setData(Array.isArray(resp?.data) ? resp.data : []);
    } catch (e) {
      console.error("Error cargando historial de oficios:", e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const enriched = useMemo(() => {
    return (data || [])
      .filter((r) => r?.fecha_oficio)
      .map((r) => {
        const d = new Date(r.fecha_oficio);
        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        return {
          ...r,
          _year: year,
          _month: month,
          _monthLabel: MESES[month - 1],
          _groupKey: `${year}-${String(month).padStart(2, "0")}`,
          _tipoLabel: labelTipo(r.tipo_oficio),
        };
      });
  }, [data]);

  const tipoOptions = useMemo(() => {
    const set = new Set();
    enriched.forEach((r) => r._tipoLabel && set.add(r._tipoLabel));
    return Array.from(set).sort();
  }, [enriched]);

  const numeroOptions = useMemo(() => {
    const set = new Set();
    enriched.forEach((r) => r.numero_oficio != null && set.add(String(r.numero_oficio)));
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [enriched]);

  const yearOptions = useMemo(() => {
    const set = new Set();
    enriched.forEach((r) => set.add(String(r._year)));
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [enriched]);

  const mesOptions = useMemo(() => MESES, []);

  const filterRecords = (name, value) => {
    const next = { ...filters, [name]: value };
    if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
      delete next[name];
    }
    setFilters(next);
  };

  const filtered = useMemo(() => {
    return enriched.filter((r) => {
      if (filters.tipo && r._tipoLabel !== filters.tipo) return false;
      if (filters.numero && String(r.numero_oficio) !== String(filters.numero)) return false;
      if (filters.year && String(r._year) !== String(filters.year)) return false;
      if (filters.mes && r._monthLabel !== filters.mes) return false;
      return true;
    });
  }, [enriched, filters]);

  const grupos = useMemo(() => {
    const g = new Map();
    for (const r of filtered) {
      if (!g.has(r._groupKey)) {
        g.set(r._groupKey, {
          key: r._groupKey,
          year: r._year,
          month: r._month,
          monthLabel: r._monthLabel,
          items: [],
          counts: new Map(),
        });
      }
      const bucket = g.get(r._groupKey);
      bucket.items.push(r);
      bucket.counts.set(
        r._tipoLabel,
        (bucket.counts.get(r._tipoLabel) || 0) + 1
      );
    }
    return Array.from(g.values()).sort((a, b) => b.key.localeCompare(a.key));
  }, [filtered]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const pages = Math.ceil(grupos.length / rowsPerPage);
    if (page > Math.max(pages - 1, 0)) setPage(0);
  }, [grupos.length, rowsPerPage, page]);

  const visibles = useMemo(() => {
    const start = page * rowsPerPage;
    return grupos.slice(start, start + rowsPerPage);
  }, [grupos, page, rowsPerPage]);

  return (
    <PageBody>
      <Box sx={{ textAlign: "center", mb: 3, mt: "-1rem" }}>
        <Typography variant="h1" sx={{ textAlign: "center", padding: "0.5rem" }}>
          HISTORIAL DE OFICIOS
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Podrá buscar por periodo todos los oficios ingresados a la plataforma SAGEC
        </Typography>
      </Box>

      <Grid
        container
        spacing={1}
        sx={{ marginBottom: "28px" }}
        alignItems="flex-start"
      >
        <Grid size={3}>
          <InputFilter
            item={"tipo"}
            name={"Tipo de oficio"}
            opt={tipoOptions}
            callback={filterRecords}
          />
        </Grid>
        <Grid size={3}>
          <InputFilter
            item={"numero"}
            name={"N° de Oficio"}
            opt={numeroOptions}
            callback={filterRecords}
          />
        </Grid>
        <Grid size={3}>
          <InputFilter
            item={"year"}
            name={"Año"}
            opt={yearOptions}
            callback={filterRecords}
          />
        </Grid>
        <Grid size={3}>
          <InputFilter
            item={"mes"}
            name={"Mes"}
            opt={mesOptions}
            callback={filterRecords}
            noSort
          />
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {grupos.length === 0 ? (
            <ListEmpty />
          ) : (
            <>
              <Stack spacing={3}>
                {visibles.map((g) => (
                  <Accordion key={g.key} defaultExpanded disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ width: "100%", justifyContent: "space-between" }}
                      >
                        <Typography variant="subtitle1" fontWeight={700}>
                          {`${g.monthLabel} ${g.year}`}
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          {Array.from(g.counts.entries()).map(([tipo, count]) => (
                            <Badge
                              key={tipo}
                              color="primary"
                              badgeContent={count}
                              max={999}
                            >
                              <Chip label={tipo} size="small" />
                            </Badge>
                          ))}
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <OficiosTable rows={g.items} />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  count={grupos.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  labelRowsPerPage="Periodos por página:"
                />
              </Box>
            </>
          )}
        </>
      )}
    </PageBody>
  );
}

function OficiosTable({ rows }) {
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const fa = new Date(a.fecha_oficio).getTime();
      const fb = new Date(b.fecha_oficio).getTime();
      return fb - fa;
    });
  }, [rows]);

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>TIPO DE OFICIO</TableCell>
            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>N° DE OFICIO</TableCell>
            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>FECHA DE OFICIO</TableCell>
            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>ARCHIVO</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row) => (
            <TableRow key={row.id_oficio} hover>
              <TableCell>{labelTipo(row.tipo_oficio)}</TableCell>
              <TableCell>{padOficio(row.numero_oficio)}</TableCell>
              <TableCell>{fmtFecha(row.fecha_oficio)}</TableCell>
              <TableCell>
                {row.url_oficio ? (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(row.url_oficio, "_blank", "noopener,noreferrer");
                    }}
                    variant="text"
                    startIcon={<PictureAsPdfIcon sx={{ color: "#111" }} />}
                  >
                    <Typography
                      sx={{
                        color: "#111",
                        textTransform: "capitalize",
                        textDecoration: "underline",
                      }}
                    >
                      Descargar oficio
                    </Typography>
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">—</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
